/*
 * Per-user tour progress.
 *
 * V1 keeps this in localStorage so the runtime can ship without waiting on the
 * API. The shape mirrors what the `user_tour_progress` table will hold, so
 * moving to the backend is a change inside this file only — nothing that
 * imports it needs to know where the record lives.
 *
 * Scoped by user id: the same browser is regularly shared between a real
 * account and a test one, and an unscoped key would hide every tour from the
 * second account.
 */
import storageService from 'services/LocalStorageService'

const PROGRESS_KEY = 'bp-tour-progress'
const SKIP_ALL_KEY = 'bp-tour-skip-all'

const scopedKey = (key, userId) => `${key}:${userId || 'anon'}`

const readProgress = userId => {
  try {
    return JSON.parse(storageService.get(scopedKey(PROGRESS_KEY, userId)) || '{}')
  } catch (e) {
    return {}
  }
}

const writeProgress = (userId, value) => {
  try {
    storageService.set(scopedKey(PROGRESS_KEY, userId), JSON.stringify(value))
  } catch (e) {
    // Private mode / quota. A tour replaying is a far smaller problem than a
    // thrown error tearing down the tree it is rendered in.
  }
}

export const getTourProgress = (userId, tourKey) =>
  readProgress(userId)[tourKey] || null

export const hasSeenTour = (userId, tourKey, version = 1) => {
  const record = getTourProgress(userId, tourKey)
  if (!record) return false
  // A bumped version re-shows the tour: the copy changed, so the fact that the
  // user saw the old one says nothing about whether they understand this one.
  return (record.version || 1) >= version
}

/*
 * Fold the server's record into the local one on login. The server is the
 * durable copy — it is what stops every tour replaying when someone changes
 * laptop or clears site data — but reads stay synchronous and local so the
 * runtime never has to wait on a request to decide whether to show a tour.
 */
export const mergeRemoteProgress = (userId, remote) => {
  if (!Array.isArray(remote)) return
  const all = readProgress(userId)
  remote.forEach(row => {
    const tourKey = row?.tourKey
    if (!tourKey) return
    const existing = all[tourKey]
    // Whichever saw the newer copy wins; a local record can be ahead if the
    // user finished a tour while the POST was failing.
    const wipesDone = existing?.status === 'completed' && ['started', 'stopped', 'missing_step'].includes(row.status)
    if (!existing || (!wipesDone && (row.version || 1) >= (existing.version || 1))) {
      all[tourKey] = {
        status: row.status,
        version: row.version || 1,
        at: row.seenAt || new Date().toISOString()
      }
    }
  })
  writeProgress(userId, all)
}

export const isWorkflowDone = (userId, tourKey, version = 1) => {
  const record = getTourProgress(userId, tourKey)
  return !!record && record.status === 'completed' && (record.version || 1) >= version
}

export const markTourSeen = (userId, tourKey, status, version = 1) => {
  const all = readProgress(userId)
  all[tourKey] = { status, version, at: new Date().toISOString() }
  writeProgress(userId, all)
}

export const isSkipAll = userId =>
  storageService.get(scopedKey(SKIP_ALL_KEY, userId)) === '1'

export const setSkipAll = (userId, value) => {
  const key = scopedKey(SKIP_ALL_KEY, userId)
  if (value) storageService.set(key, '1')
  else storageService.remove(key)
}

// Exposed for QA: `window.__bpTours.reset()` replays everything.
export const resetTours = userId => {
  storageService.remove(scopedKey(PROGRESS_KEY, userId))
  storageService.remove(scopedKey(SKIP_ALL_KEY, userId))
}
